import React, { useContext, useState } from "react";
import { Link, Redirect } from "react-router-dom";
import axios from "axios";

import { SettingsContext } from '../contexts/index.jsx';
import { getStorageKey, setStorageKey } from "../core/utils/storage";
import { getDeviceId, REFRESH_TOKEN_KEY } from "../core/utils/deviceId";
import { Alert, Loading, Separator } from "../components";
import { useMerchantStripe } from "../merchants";
import { SocialButtons } from "./CreateAccount/index.jsx";
import { LoginForm, RCLoginForm } from "./SignIn/index.jsx";

const SignIn = ({
  authentication,
  user,
  hideRegisterOption = false,
  fullWidth = false,
  // Supplied by AuthenticationLayout from the path, so it is "sign-in" only on
  // the standalone /sign-in page — AssignRequest embeds SignIn without it.
  type,
}) => {
  const { isMerchant, isRetailCircle, iframeQueryParams } = useContext(
    SettingsContext
  );
  const [status, setStatus] = useState("initial");
  const [provider, setProvider] = useState();
  const [retailError, setRetailError] = useState();
  const { goToStripeManagementPage } = useMerchantStripe();
  // Signing in to accept a team invite (/invite/:token stored it on the way here).
  const toInvite = String(getStorageKey("afterLoginRedirect") || "").startsWith("/invite/");

  if (user && status === "initial") {
    const path = isMerchant ? `/requests/orders?${iframeQueryParams}` : "/";

    return <Redirect to={path} />;
  }

  if (isMerchant) {
    return null;
  }

  // DAO Watches: email + password login (unchanged)
  const getToken = async (provider, credentials) => {
    setStatus("submitting");
    setProvider(provider);
    setRetailError(null);

    try {
      const response = await axios.post("/api/tokens/create", {
        authentication_provider: provider,
        credentials: credentials,
      });

      const user = response.data.user;
      const token = response.data.token;

      if (!isRetailCircle) {
        authentication.login(user, response.data.token);
      } else {
        if (!user.email_verified_at) {
          setRetailError(
            "If already registered, verify email by opening the link sent to inbox"
          );

          return;
        }

        goToStripeManagementPage(user, token, false, true);
      }
    } catch (err) {
      setStatus("error");
    }
  };

  const handleSocialLogin = (data) => {
    getToken(data.type, {
      access_token: data.access_token,
    });
  };

  const handlePasswordLogin = async (data) => {
    await getToken("retailcircle", data);
  };

  // RC: OTP login — verify OTP and login in one step
  const handleRCOtpVerified = async (phoneNumber, otpCode) => {
    try {
      // Single call: verify OTP + get tokens
      const tokenResponse = await axios.post("/app_api/rc-users/signin-with-tokens", {
        phoneNumber,
        otpCode,
        // Stable per-browser id — must match on every later refresh-token call.
        deviceId: getDeviceId(),
      });

      if (tokenResponse.data.success) {
        const user = tokenResponse.data.user;

        // The backend decides who may sign in from the web app (staff, or an
        // open invite). An invitee is only switched on when they accept, so
        // don't turn them away on the way to the invite page.
        if (!user.access_rc_web && !toInvite) {
          throw new Error("Your account is not approved for RC webapp access yet. Please contact support.");
        }

        // Keep the 30-day refresh token so UserContext can silently re-mint the
        // access token instead of bouncing the user back here on the first 401.
        if (tokenResponse.data.refreshToken) {
          setStorageKey(REFRESH_TOKEN_KEY, tokenResponse.data.refreshToken);
        }

        authentication.login(user, tokenResponse.data.token);
      } else {
        throw new Error(tokenResponse.data.message || "Login failed.");
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Login failed.";
      throw new Error(msg);
    }
  };

  // Retail Circle: OTP-based login
  if (isRetailCircle) {
    return (
      <>
        {status === "error" && (
          <Alert
            type="error"
            message="Login failed"
            description="Please try again."
          />
        )}

        {toInvite && (
          <p className="small-text">
            Sign in with your mobile number to accept your invitation. No account yet? Ask the store owner to add you by phone.
          </p>
        )}

        <RCLoginForm
          onOtpVerified={handleRCOtpVerified}
          standalone={type === "sign-in"}
        />
      </>
    );
  }

  // DAO Watches: original email + password login (completely unchanged)
  return (
    <>
      {status !== "submitting" && (
        <>
          <SocialButtons onSuccess={handleSocialLogin} fullWidth={fullWidth} />
          <Separator text="OR" />
        </>
      )}

      {status === "submitting" && provider !== "retailcircle" && (
        <Loading type="loading" className="mb-4" text="Authenticating..." />
      )}

      {status === "error" && (
        <Alert
          type="error"
          message="Invalid credentials"
          description="Please try again with different credentials."
        />
      )}

      <LoginForm onSubmit={handlePasswordLogin} retailError={retailError} />

      {!hideRegisterOption && (
        <>
          <p className="small-text">
            You don't have an account?{" "}
            <Link to={`/create-account?${iframeQueryParams}`}>
              Register here
            </Link>
          </p>

          <p className="small-text mb-4 mb-xxl-0">
            Forgotten your password? You can{" "}
            <Link to={`/recover-account?${iframeQueryParams}`}>
              recover your account
            </Link>
            .
          </p>
        </>
      )}
    </>
  );
};

export default SignIn;
